import styled from '@emotion/styled'
import React from 'react'

import Button from 'components/Button'
import CircularLoader from 'components/CircularLoader'
import { Portal } from 'components/Portal'
import { InfoIcon } from 'components/icons'
import { useInstallations } from 'hooks/useInstallations'
import { ActionButtonsPortalContext } from 'layouts/DefaultLayout'
import { Tooltip } from 'layouts/components/SharedStyledComponents'
import { formatDate } from 'lib/formatDate'
import { useAppStore } from 'lib/store'

const CrawlStatusRow = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
	padding-top: ${({ theme }) => theme.spaces.space4};
	position: relative;
	gap: ${({ theme }) => theme.spaces.space4};
`

const RemoveButton = styled(Button)`
	position: absolute;
	right: 0;
	margin: 0;
	padding: 0;
	width: 70px;
	color: ${({ theme }) => theme.colors.light.toast.error.background};
`

const StatusText = styled.span`
	${({ theme }) => theme.typography.smallcaps};
	color: #7a7a7a;
	display: flex;
	align-items: center;
`
const CrawlDetails = styled.div`
	display: flex;
	width: 100%;
	flex-direction: column;
	position: relative;
`

const CountedProductsInfo = styled.p`
	margin: 0;
	font-size: ${({ theme }) => theme.fontSizes.size14};
	font-weight: 700;
	padding-bottom: ${({ theme }) => theme.spaces.space3};
`

const StatusCircle = styled.div<{ status: 'success' | 'error' }>`
	width: 12px;
	height: 12px;
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	background-color: ${({ theme, status }) =>
		status === 'success' ? theme.colors.light.success : theme.colors.light.toast.error.background};
`

const Website = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	width: 250px;
`

const IconOuter = styled.div`
	position: absolute;
	right: 0;
	padding: 0;
`

const StyledTooltip = styled(Tooltip)`
	width: 210px;
	padding: ${({ theme }) => theme.spaces.space4};
	white-space: normal;
	top: -40px;
	right: 0;
`

const CrawledWebsiteItemOuter = styled.div`
	width: 100%;
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	justify-content: flex-start;
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	gap: ${({ theme }) => theme.spaces.space4};
	${({ theme }) => theme.typography.caption};
	padding: ${({ theme }) => theme.spaces.space5};
	background-color: ${({ theme }) => theme.colors.light.grayscale[15]};
	border: 1px solid ${({ theme }) => theme.colors.light.borderColor};
	margin-bottom: ${({ theme }) => theme.spaces.space4};
`

type Props = {
	handleRecommendationsDisconnect: () => void
	handleRetryCrawl: (domain: string | null) => void
	crawlDate?: number | null
	isPending: boolean
	failedCrawl: boolean
	foundProductsCount?: number
	crawledWebsite: string | null
}

function CrawledWebsiteItem({
	handleRecommendationsDisconnect,
	handleRetryCrawl,
	crawlDate,
	isPending,
	failedCrawl,
	foundProductsCount,
	crawledWebsite,
}: Props) {
	const store = useAppStore()
	const [isTooltipOpen, setIsTooltipOpen] = React.useState(false)
	const { installations, areInstallationsLoading } = useInstallations()

	const formattedCrawlDate = React.useMemo(() => {
		if (!crawlDate) {
			return null
		}

		return formatDate(crawlDate)
	}, [crawlDate])

	const displayConfirmationModal = () => store.dispatch({ type: 'currentModal/set', payload: 'ConfirmDisableModal' })

	const handleTooltipVisibility = (event: React.KeyboardEvent) => {
		event.preventDefault()
		if (event.key === 'Enter' || event.key === ' ') {
			setIsTooltipOpen((current) => !current)
		} else if (event.key === 'Escape') {
			setIsTooltipOpen(false)
		}
	}

	if (areInstallationsLoading || !installations) {
		return null
	}

	return (
		<>
			<CrawledWebsiteItemOuter>
				{isPending ? (
					<>
						<CircularLoader size="medium" data-testid="loader" />
						<Website>{crawledWebsite}</Website>
						<span>{"This may take a while. We'll email you once we finish searching your website for products."}</span>
					</>
				) : (
					<CrawlDetails>
						<CountedProductsInfo>
							{failedCrawl
								? "We couldn't find any products on"
								: `${foundProductsCount} product${
										typeof foundProductsCount === 'number' && foundProductsCount > 1 ? 's' : ''
								  }`}
						</CountedProductsInfo>
						{!failedCrawl && (
							<IconOuter
								onMouseOver={() => setIsTooltipOpen(true)}
								onMouseLeave={() => setIsTooltipOpen(false)}
								tabIndex={0}
								role="button"
								aria-haspopup="true"
								aria-expanded={isTooltipOpen}
								aria-controls="recommendations-hint-tooltip"
								onKeyUp={handleTooltipVisibility}
								aria-label='Read more about "Product Recommendations"'
							>
								<InfoIcon />
							</IconOuter>
						)}
						<Website>{crawledWebsite}</Website>
						<CrawlStatusRow>
							<StatusCircle status={failedCrawl ? 'error' : 'success'} data-testid="crawl-status-circle" />
							<StatusText>
								{failedCrawl ? 'Failed' : `Updated: ${formattedCrawlDate?.day}, ${formattedCrawlDate?.time}`}
							</StatusText>
							<RemoveButton
								kind="text"
								type="button"
								disableHover
								onClick={failedCrawl ? handleRecommendationsDisconnect : displayConfirmationModal}
							>
								{failedCrawl ? 'Remove' : 'Turn off'}
							</RemoveButton>
						</CrawlStatusRow>
					</CrawlDetails>
				)}
			</CrawledWebsiteItemOuter>
			{isTooltipOpen && (
				<StyledTooltip
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3, ease: [0.39, 0.54, 0.12, 0.94] }}
					id="recommendations-hint-tooltip"
				>
					{
						"We automatically update your website's products daily to ensure that your new products are included as well."
					}
				</StyledTooltip>
			)}
			{failedCrawl && (
				<Portal context={ActionButtonsPortalContext}>
					<Button fullWidth kind="primary" type="button" disableHover onClick={() => handleRetryCrawl(crawledWebsite)}>
						Try again
					</Button>
				</Portal>
			)}
		</>
	)
}

export default CrawledWebsiteItem
